import { NavLink, useLocation, Link } from 'react-router-dom'
import { 
  LayoutDashboard, 
  Camera, 
  Truck, 
  Calendar, 
  FileText,
  Settings,
  Receipt,
  Menu,
  X,
  Scale
} from 'lucide-react'
import { useState } from 'react'
import { cn } from '../../lib/utils'
import { useTheme } from '../../context/ThemeContext'
import { useAuth } from '../../context/AuthContext'
import { Button } from '../ui/button'
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet'

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/expenses', icon: Receipt, label: 'Expenses' },
  { path: '/loads', icon: Truck, label: 'Loads' },
  { path: '/per-diem', icon: Calendar, label: 'Per Diem' },
  { path: '/reports', icon: FileText, label: 'Reports' },
]

const sidebarItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/expenses', icon: Receipt, label: 'Expenses' },
  { path: '/loads', icon: Truck, label: 'Loads / Revenue' },
  { path: '/per-diem', icon: Calendar, label: 'Per Diem Calendar' },
  { path: '/reports', icon: FileText, label: 'Reports' },
  { path: '/settings', icon: Settings, label: 'Settings' },
  { path: '/legal', icon: Scale, label: 'Legal / Terms' },
]

export function MobileShell({ children }) {
  const location = useLocation()
  const { theme, toggleTheme, isDark } = useTheme()
  const { user, logout } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 border-r border-border bg-card">
        <div className="flex items-center h-16 px-4 border-b border-border gap-3">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/d9a36397-75b6-45a0-b476-0118e30707e5/images/7feac39be335da8e6a2ca874b553dd78b4b011049f274c8c33789ba32c56e471.png" 
            alt="Driver PNL Logo" 
            className="w-12 h-12 rounded-lg object-cover"
          />
          <h1 className="text-3xl font-bold tracking-tight text-foreground font-['Barlow_Condensed']">
            DRIVER PNL
          </h1>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {sidebarItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => cn(
                'sidebar-link',
                isActive && 'active'
              )}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm">
              <p className="font-medium text-foreground">{user?.name}</p>
              <p className="text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
          <div className="flex gap-2 mb-3">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleTheme}
              className="flex-1"
              data-testid="theme-toggle-desktop"
            >
              {isDark ? '☀️ Light' : '🌙 Dark'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="flex-1"
              data-testid="logout-btn-desktop"
            >
              Logout
            </Button>
          </div>
          <Link 
            to="/legal" 
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors"
            data-testid="legal-link-desktop"
          >
            <Scale className="w-3 h-3" />
            Terms & Privacy
          </Link>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 glass border-b border-border z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/d9a36397-75b6-45a0-b476-0118e30707e5/images/7feac39be335da8e6a2ca874b553dd78b4b011049f274c8c33789ba32c56e471.png" 
            alt="Driver PNL Logo" 
            className="w-10 h-10 rounded-lg object-cover"
          />
          <h1 className="text-2xl font-bold tracking-tight text-foreground font-['Barlow_Condensed']">
            DRIVER PNL
          </h1>
        </div>
        
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 p-0">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between h-14 px-4 border-b border-border">
                <span className="font-bold">Menu</span>
              </div>
              
              <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                {sidebarItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => cn(
                      'flex items-center gap-3 px-3 py-3 rounded-lg transition-colors',
                      isActive 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-muted-foreground hover:bg-secondary'
                    )}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="w-5 h-5" strokeWidth={1.5} />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                ))}
              </nav>
              
              <div className="p-4 border-t border-border space-y-3">
                <div className="text-sm">
                  <p className="font-medium text-foreground">{user?.name}</p>
                  <p className="text-muted-foreground capitalize">{user?.role}</p>
                </div>
                <Button
                  variant="outline"
                  onClick={toggleTheme}
                  className="w-full"
                  data-testid="theme-toggle-mobile"
                >
                  {isDark ? '☀️ Switch to Light' : '🌙 Switch to Dark'}
                </Button>
                <Button
                  variant="outline"
                  onClick={logout}
                  className="w-full"
                  data-testid="logout-btn-mobile"
                >
                  Logout
                </Button>
                <Link 
                  to="/legal" 
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors pt-2"
                  data-testid="legal-link-mobile"
                >
                  <Scale className="w-3 h-3" />
                  Terms & Privacy
                </Link>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64 pt-14 lg:pt-0 pb-20 lg:pb-0 min-h-screen">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden bottom-nav glass safe-bottom">
        <div className="flex justify-around">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={cn('bottom-nav-item flex-1', isActive && 'active')}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              >
                <item.icon 
                  className={cn('w-6 h-6', isActive && 'glow-primary')} 
                  strokeWidth={1.5} 
                />
                <span className="text-xs font-medium">{item.label}</span>
              </NavLink>
            )
          })}
        </div>
        {/* Spacer for "Made with Emergent" badge */}
        <div className="h-16 bg-background"></div>
      </nav>
    </div>
  )
}
